import React, { useState, useEffect } from 'react';
import Card from '../../../components/Card';
import TextInput from '../../../components/TextInput';
import cn from "classnames";
import styles from './CostsInformations.module.sass';
import DualButton from '../../../components/DualButton';

const CostsInformations = ({ formation, onFormationChange, loading, visible = true }) => {
  // États locaux pour gérer l'affichage des champs conditionnels
  const [fraisHebergement, setFraisHebergement] = useState('Non');
  const [fraisRestauration, setFraisRestauration] = useState('Non');
  const [premierEquipement, setPremierEquipement] = useState('Non');
  const [fraisMobilite, setFraisMobilite] = useState('Non');

  // Initialiser les états DualButton selon les valeurs existantes
  useEffect(() => {
    if (formation) {
      console.log('FormationCreate - Formation data received:', formation);
      
      // Frais hébergement
      if ((formation.numberOfAccommodations && formation.numberOfAccommodations > 0) || formation.fraisHebergement === true) {
        setFraisHebergement('Oui');
      } else {
        setFraisHebergement('Non');
      }

      // Frais restauration
      if ((formation.numberOfMeals && formation.numberOfMeals > 0) || formation.fraisRestauration === true) {
        setFraisRestauration('Oui');
      } else {
        setFraisRestauration('Non');
      }

      // Premier équipement pédagogique
      if (formation.amountOfInitialEquipment === true) {
        setPremierEquipement('Oui');
      } else {
        setPremierEquipement('Non');
      }

      // Frais mobilité internationale
      if (formation.mentionOfInternationalMobility === true) {
        setFraisMobilite('Oui');
      } else {
        setFraisMobilite('Non');
      }

      console.log('FormationCreate - DualButton states initialized:', {
        fraisHebergement: (formation.numberOfAccommodations && formation.numberOfAccommodations > 0) || formation.fraisHebergement === true ? 'Oui' : 'Non',
        fraisRestauration: (formation.numberOfMeals && formation.numberOfMeals > 0) || formation.fraisRestauration === true ? 'Oui' : 'Non',
        premierEquipement: formation.amountOfInitialEquipment === true ? 'Oui' : 'Non',
        fraisMobilite: formation.mentionOfInternationalMobility === true ? 'Oui' : 'Non'
      });
      console.log('FormationCreate - Backend field values:', {
        numberOfAccommodations: formation.numberOfAccommodations,
        numberOfMeals: formation.numberOfMeals,
        amountOfInitialEquipment: formation.amountOfInitialEquipment,
        mentionOfInternationalMobility: formation.mentionOfInternationalMobility
      });
    }
  }, [formation]);

  if (!visible) return null;

  const handleDualButtonChange = (fieldName, value) => {
    // Mettre à jour l'état local
    switch (fieldName) {
      case 'fraisHebergement':
        setFraisHebergement(value);
        break;
      case 'fraisRestauration':
        setFraisRestauration(value);
        break;
      case 'premierEquipement':
        setPremierEquipement(value);
        break;
      case 'fraisMobilite':
        setFraisMobilite(value);
        break;
    }

    // Sauvegarder la valeur du DualButton avec les bons noms de champs
    let targetFieldName = fieldName;
    if (fieldName === 'premierEquipement') {
      targetFieldName = 'amountOfInitialEquipment';
    } else if (fieldName === 'fraisMobilite') {
      targetFieldName = 'mentionOfInternationalMobility';
    }

    console.log(`FormationCreate - DualButton changed: ${fieldName} → ${targetFieldName} = ${value} (boolean: ${value === 'Oui'})`);
    if (onFormationChange) {
      onFormationChange({
        target: {
          name: targetFieldName,
          value: value === 'Oui'
        }
      });
    }
  };

  const handleInputChange = (e) => {
    // Empêcher les valeurs négatives pour les champs numériques
    if (e.target.value < 0) {
      return;
    }

    if (onFormationChange) {
      onFormationChange(e);
    }
  };

  return (
    <Card className={styles.card} title="Configuration du contrat d'apprentissage" classTitle="title-blue">

      {/* Frais hébergement */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Frais hébergement</h3>
          <DualButton
            option1="Oui"
            option2="Non"
            value={fraisHebergement}
            onChange={(value) => handleDualButtonChange('fraisHebergement', value)}
            multiSelect={false}
          />
        </div>
        {fraisHebergement === 'Oui' && (
          <TextInput
            className={styles.field}
            label="Nombre de nuits annuelles approximatives"
            name="numberOfAccommodations"
            type="number"
            step="1"
            min="0"
            placeholder="Nombre de nuits annuelles approximatives"
            value={formation?.numberOfAccommodations || ''}
            onChange={handleInputChange}
          />
        )}
      </div>

      {/* Premier équipement pédagogique */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Premier équipement pédagogique</h3>
          <DualButton
            option1="Oui"
            option2="Non"
            value={premierEquipement}
            onChange={(value) => handleDualButtonChange('premierEquipement', value)}
            multiSelect={false}
          />
        </div>
      </div>

      {/* Frais restauration */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Frais restauration</h3>
          <DualButton
            option1="Oui"
            option2="Non"
            value={fraisRestauration}
            onChange={(value) => handleDualButtonChange('fraisRestauration', value)}
            multiSelect={false}
          />
        </div>
        {fraisRestauration === 'Oui' && (
          <TextInput
            className={styles.field}
            label="Nombre de repas annuels approximatifs"
            name="numberOfMeals"
            type="number"
            step="1"
            min="0"
            placeholder="Nombre de repas annuels approximatifs"
            value={formation?.numberOfMeals || ''}
            onChange={handleInputChange}
          />
        )}
      </div>

      {/* Frais liés à la mobilité internationale */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Frais liés à la mobilité internationale</h3>
          <DualButton
            option1="Oui"
            option2="Non"
            value={fraisMobilite}
            onChange={(value) => handleDualButtonChange('fraisMobilite', value)}
            multiSelect={false}
          />
        </div>
      </div>

    </Card>
  );
};

export default CostsInformations;
